import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Wrench, AlertTriangle, DollarSign, CalendarDays } from "lucide-react";
import { Button } from "../../components/UI/Button";
import TextInput from "../../components/UI/TextInput";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Table from "../../components/UI/Table";
import Modal from "../../components/UI/Modal";
import Selection from "../../components/UI/Selection";
import { httpClient } from "../../../infrastructure/api/http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import { useAuthStore } from "../../stores/auth-store";

type MaintenanceType = "REPAIR" | "SCHEDULED_CHECK" | "PART_REPLACEMENT";

const TYPE_LABELS: Record<MaintenanceType, string> = {
  REPAIR: "고장 수리",
  SCHEDULED_CHECK: "정기 점검",
  PART_REPLACEMENT: "부품 교체",
};

const TYPE_DOT_COLORS: Record<MaintenanceType, string> = {
  REPAIR: "bg-red-400",
  SCHEDULED_CHECK: "bg-blue-400",
  PART_REPLACEMENT: "bg-yellow-400",
};

type MaintenanceHistory = {
  id: number;
  machineId: number;
  type: MaintenanceType;
  title: string;
  description: string | null;
  cost: number | null;
  maintainedAt: string;
  createdAt: string | null;
};

type MachineInfo = {
  machineId: number;
  modelName: string;
  ipAddress: string;
};

type FormData = {
  machineId: string;
  type: MaintenanceType;
  title: string;
  description: string;
  cost: string;
  maintainedAt: string;
};

const emptyForm: FormData = {
  machineId: "",
  type: "SCHEDULED_CHECK",
  title: "",
  description: "",
  cost: "",
  maintainedAt: new Date().toISOString().slice(0, 10),
};

const MaintenanceManagement = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  const [histories, setHistories] = useState<MaintenanceHistory[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState<string>("");
  const [filterMachineId, setFilterMachineId] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [stats, setStats] = useState<{
    totalCount: number;
    monthlyRepairCount: number;
    monthlyCost: number;
    totalCost: number;
  } | null>(null);

  const fetchStats = async () => {
    if (!companyId) return;
    try {
      const url = NEW_API_ENDPOINTS.companyMaintenanceController.GET_STATS(companyId);
      const res = await httpClient.get<{ data: typeof stats }>(url);
      setStats(res.data.data);
    } catch (e) {
      console.error("통계 조회 실패:", e);
    }
  };

  // 장비 목록 조회
  const fetchMachines = async () => {
    if (!companyId) return;
    try {
      const url = NEW_API_ENDPOINTS.companyRouterMachinesController.GET(companyId);
      const res = await httpClient.get<{ data: any[] }>(url);
      const allMachines: MachineInfo[] = (res.data.data || []).flatMap(
        (router: any) =>
          (router.machines || []).map((m: any) => ({
            machineId: m.machineId,
            modelName: m.displayName ?? m.modelName,
            ipAddress: m.ipAddress,
          }))
      );
      setMachines(allMachines);
    } catch (e) {
      console.error("장비 목록 조회 실패:", e);
    }
  };

  const fetchHistories = async () => {
    if (!companyId) return;
    try {
      const url = NEW_API_ENDPOINTS.companyMaintenanceController.GET(
        companyId,
        filterMachineId ? Number(filterMachineId) : undefined,
        filterType || undefined,
        page,
        20
      );
      const res = await httpClient.get<{
        data: { content: MaintenanceHistory[]; totalElements: number };
        errorCode: string | null;
      }>(url);
      setHistories(res.data.data.content || []);
      setTotalElements(res.data.data.totalElements || 0);
    } catch (e) {
      console.error("설비 이력 조회 실패:", e);
    }
  };

  useEffect(() => {
    fetchMachines();
    fetchStats();
  }, [companyId]);

  useEffect(() => {
    fetchHistories();
  }, [companyId, page, filterType, filterMachineId]);

  const getMachineName = (machineId: number): string => {
    const machine = machines.find((m) => m.machineId === machineId);
    return machine ? machine.modelName : `설비 #${machineId}`;
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (item: MaintenanceHistory) => {
    setEditingId(item.id);
    setForm({
      machineId: String(item.machineId),
      type: item.type,
      title: item.title,
      description: item.description || "",
      cost: item.cost != null ? String(item.cost) : "",
      maintainedAt: item.maintainedAt,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!companyId) return;
    if (!form.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }
    if (!form.machineId) {
      alert("대상 설비를 선택해주세요.");
      return;
    }

    setLoading(true);
    try {
      const body = {
        machineId: Number(form.machineId),
        type: form.type,
        title: form.title,
        description: form.description || null,
        cost: form.cost ? Number(form.cost) : null,
        maintainedAt: form.maintainedAt,
      };

      if (editingId) {
        const url = NEW_API_ENDPOINTS.companyMaintenanceController.PUT(companyId, editingId);
        await httpClient.put(url, body);
      } else {
        const url = NEW_API_ENDPOINTS.companyMaintenanceController.POST(companyId);
        await httpClient.post(url, body);
      }

      setIsModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      await fetchHistories();
      await fetchStats();
    } catch (e: any) {
      console.error("저장 실패:", e);
      alert(e.message || "저장에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!companyId) return;
    if (!window.confirm("삭제하시겠습니까?")) return;

    try {
      const url = NEW_API_ENDPOINTS.companyMaintenanceController.DELETE(companyId, id);
      await httpClient.delete(url);
      await fetchHistories();
      await fetchStats();
    } catch (e) {
      console.error("삭제 실패:", e);
      alert("삭제에 실패했습니다.");
    }
  };

  const typeFilterOptions = [
    { value: "", name: "전체 유형" },
    { value: "REPAIR", name: "고장 수리" },
    { value: "SCHEDULED_CHECK", name: "정기 점검" },
    { value: "PART_REPLACEMENT", name: "부품 교체" },
  ];

  const machineFilterOptions = [
    { value: "", name: "전체 설비" },
    ...machines.map((m) => ({
      value: String(m.machineId),
      name: `${m.modelName} (${m.ipAddress})`,
    })),
  ];

  const formMachineOptions = [
    { value: "", name: "설비를 선택하세요" },
    ...machines.map((m) => ({
      value: String(m.machineId),
      name: `${m.modelName} (${m.ipAddress})`,
    })),
  ];

  const formTypeOptions = [
    { value: "SCHEDULED_CHECK", name: "정기 점검" },
    { value: "REPAIR", name: "고장 수리" },
    { value: "PART_REPLACEMENT", name: "부품 교체" },
  ];

  return (
    <>
      {isModalOpen && (
        <Modal
          setIsModalVisible={setIsModalOpen}
          title={editingId ? "유지보수 이력 수정" : "유지보수 이력 등록"}
        >
          <>
            <div className="w-[70vw] max-w-[520px]"></div>
            <div className="space-y-4 mt-[20px]">
              <div>
                <label className="block text-sm text-start font-semibold text-[#444] mb-2">대상 설비 *</label>
                <div className="h-10">
                  <Selection
                    selectedOption={form.machineId}
                    setSelectedOption={(val) =>
                      setForm({ ...form, machineId: String(val) })
                    }
                    options={formMachineOptions}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-start font-semibold text-[#444] mb-2">유지보수 유형 *</label>
                  <div className="h-10">
                    <Selection
                      selectedOption={form.type}
                      setSelectedOption={(val) =>
                        setForm({ ...form, type: val as MaintenanceType })
                      }
                      options={formTypeOptions}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-start font-semibold text-[#444] mb-2">수행일 *</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-[0.4rem] p-2 text-sm h-10"
                    value={form.maintainedAt}
                    onChange={(e) => setForm({ ...form, maintainedAt: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-start font-semibold text-[#444] mb-2">내용 *</label>
                <TextInput
                  placeholder="유지보수 내용을 간단히 입력하세요"
                  value={form.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, title: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm text-start font-semibold text-[#444] mb-2">상세 내용</label>
                <textarea
                  className="w-full border border-gray-300 rounded-[0.4rem] p-2 text-sm resize-vertical min-h-[80px]"
                  placeholder="상세한 작업 내용을 입력하세요 (선택)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-start font-semibold text-[#444] mb-2">비용 (원)</label>
                <TextInput
                  placeholder="비용을 입력하세요 (선택)"
                  value={form.cost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, cost: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                취소
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "저장 중..." : editingId ? "수정" : "등록"}
              </Button>
            </div>
          </>
        </Modal>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[24px] flex justify-between items-center">
            <h2 className="text-start text-[1.375rem] font-semibold">유지보수 이력</h2>
            <Button onClick={handleOpenCreate} className="flex items-center">
              <Plus className="w-4 h-4 mr-1" />
              이력 등록
            </Button>
          </div>

          {/* 요약 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-white rounded-[10px] p-4 border border-[#E8E8E8]">
              <div className="text-[12px] text-[#888] mb-1">전체 이력</div>
              <div className="gap-1">
                <span className="text-[22px] font-bold text-[#252525]">{stats?.totalCount ?? 0}</span>
                <span className="text-[12px] text-[#888]">건</span>
              </div>
            </div>
            <div className="bg-white rounded-[10px] p-4 border border-[#E8E8E8]">
              <div className="text-[12px] text-[#888] mb-1">이번 달 수리</div>
              <div className="gap-1">
                <span className="text-[22px] font-bold text-[#dc2626]">{stats?.monthlyRepairCount ?? 0}</span>
                <span className="text-[12px] text-[#888]">건</span>
              </div>
            </div>
            <div className="bg-white rounded-[10px] p-4 border border-[#E8E8E8]">
              <div className="text-[12px] text-[#888] mb-1">이번 달 비용</div>
              <div className="gap-1">
                <span className="text-[22px] font-bold text-[#252525]">{(stats?.monthlyCost ?? 0).toLocaleString()}</span>
                <span className="text-[12px] text-[#888]">원</span>
              </div>
            </div>
            <div className="bg-white rounded-[10px] p-4 border border-[#E8E8E8]">
              <div className="text-[12px] text-[#888] mb-1">총 누적 비용</div>
              <div className="gap-1">
                <span className="text-[22px] font-bold text-[#252525]">{(stats?.totalCost ?? 0).toLocaleString()}</span>
                <span className="text-[12px] text-[#888]">원</span>
              </div>
            </div>
          </div>

          {/* 필터 */}
          <div className="bg-white rounded-[10px] p-5 border border-[#E8E8E8]">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-[#666] whitespace-nowrap">설비</span>
                <div className="w-[200px] h-10">
                  <Selection
                    selectedOption={filterMachineId}
                    setSelectedOption={setFilterMachineId}
                    options={machineFilterOptions}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-[#666] whitespace-nowrap">유형</span>
                <div className="w-[150px] h-10">
                  <Selection
                    selectedOption={filterType}
                    setSelectedOption={setFilterType}
                    options={typeFilterOptions}
                  />
                </div>
              </div>
            </div>

            {/* 테이블 */}
            <div className="text-[13px]">
              <Table
                columnWidths={["10%", "19%", "10%", "30%", "10%", "11%", "13"]}
                tabledata={{
                  head: [
                    <span className="block text-center">수행일</span>,
                    <span className="block text-center">설비명</span>,
                    <span className="block text-center">유형</span>,
                    <span className="block text-center">내용</span>,
                    <span className="block text-center">비용</span>,
                    <span className="block text-center">등록일</span>,
                    <></>,
                  ],
                  body: histories.map((item) => [
                    <span className="text-[#888]">{item.maintainedAt}</span>,
                    <span className="block text-center">{getMachineName(item.machineId)}</span>,
                    <span className="inline-flex items-center gap-[6px] px-[10px] py-[3px] rounded-full text-[12px] font-medium bg-[#f5f5f5] text-[#555]">
                      <span className={`w-[6px] h-[6px] rounded-full ${TYPE_DOT_COLORS[item.type] || "bg-gray-400"}`} />
                      {TYPE_LABELS[item.type] || item.type}
                    </span>,
                    <>{item.title}</>,
                    <span className="block text-right font-medium">
                      {item.cost != null ? `${item.cost.toLocaleString()}원` : "-"}
                    </span>,
                    <span className="block text-center text-[#888]">
                      {item.createdAt ? item.createdAt.toString().slice(0, 10) : "-"}
                    </span>,
                    <div className="flex gap-[6px] opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleOpenEdit(item)}
                        className="p-1 text-[#888] hover:text-[#4285f4] transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1 text-[#888] hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>,
                  ]),
                }}
              />
            </div>

            {/* 페이지네이션 */}
            {totalElements > 20 && (
              <div className="flex justify-center items-center gap-1 mt-4">
                {Array.from({ length: Math.ceil(totalElements / 20) }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`w-8 h-8 rounded-[6px] text-[13px] border ${
                      page === i
                        ? "bg-[#4285F4] text-white border-[#4285F4]"
                        : "bg-white text-[#333] border-[#ddd] hover:bg-[#f5f5f5]"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <span className="text-[13px] text-[#888] ml-3">총 {totalElements}건</span>
              </div>
            )}
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default MaintenanceManagement;